/* eslint-disable @stencil-community/prefer-vdom-listener */

import {
    Component,
    ComponentInterface,
    Element,
    Event,
    EventEmitter,
    h,
    Host,
    Listen,
    Method,
    Prop,
    State,
    Watch
} from "@stencil/core";
import { debounce } from "../../utils/debounce";

const stepsPerKey = {
    vertical: { ArrowUp: -1, ArrowDown: 1 },
    horizontal: { ArrowLeft: -1, ArrowRight: 1 }
};

@Component({
    tag: "uic-tab-list",
    styleUrl: "tab-list.scss"
})
export class TabList implements ComponentInterface {
    @Element() host: HTMLUicTabListElement;

    /**
     * Fired when the selected tab changes.
     */
    @Event({ eventName: "uicChange", cancelable: true })
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    change: EventEmitter<any>;

    /**
     * If set to `true`, the direction of the tab list is vertical.
     */
    @Prop({ reflect: true }) vertical = false;

    /**
     * The value of the selected tab. Overrides tabs with `selected` set to `true`.
     */
    @Prop({ reflect: true, mutable: true }) value: string;

    /**
     * The color variant.
     */
    @Prop({ reflect: true }) variant: "main" | "content" | "sub" = "main";

    /**
     * Prevent the user from clicking a tab without a check from a parent component.
     * For example a tab opens an iframe where the user changes a name and doesn't save it, the parent component can then
     * interrupt the navigation.
     */
    @Prop() preventAutoNavigation = false;

    /**
     * When the selection indicator should be set manually from the outside (with updateSelectionIndicator(tab)),
     * this can block the default behavior
     */
    @Prop() manualSelectionIndicator = false;

    /**
     * Indicates if current tab-list is top-most in a hierarchy of tab lists.
     */
    @State() isTop: boolean;

    protected registeredTabs: HTMLUicTabElement[] = [];

    protected selectedTab: HTMLUicTabElement;

    private selectionIndicator: HTMLDivElement;

    private observer: MutationObserver;

    private readonly debouncedUpdateSelectionIndicator = debounce(() => this.updateSelectionIndicator(this.selectedTab), 40);

    @Watch("value")
    onValueChanged() {
        const tab = this.registeredTabs.find((tab) => tab.value === this.value);

        if (tab) {
            tab.selected = true;
        }

        this.change.emit(this.value);
    }

    /**
     * If the click event was prevented by preventAutoNavigation, this method can be called to resume the functionality.
     * @param event caught click event, for example the one happening on the parent horizontal-menu-container
     */
    @Method()
    async delayedOnClick(event) {
        if (!event) {
            return;
        }

        const tab = (event.target as HTMLElement).closest("uic-tab");

        if (tab && tab !== this.selectedTab) {
            tab.selected = true;
            tab.focus();
        }
    }

    @Listen("focusin")
    onFocusIn() {
        const handleKeydown = (event) => this.handleKeydown(event);
        const handleFocusOut = () => {
            this.host.removeEventListener("keydown", handleKeydown);
            this.host.removeEventListener("focusout", handleFocusOut);
        };

        this.host.addEventListener("keydown", handleKeydown);
        this.host.addEventListener("focusout", handleFocusOut);
    }

    @Listen("resize", { target: "window", passive: true })
    handleResize() {
        if (!this.manualSelectionIndicator) {
            this.debouncedUpdateSelectionIndicator();
        }
    }

    connectedCallback() {
        this.isTop = this.host.parentElement.closest("uic-tab-list") == null;
    }

    componentDidLoad() {
        // The watcher only accounts for added nodes, we also need to account for removed nodes
        this.observer = new MutationObserver((allMutations) => {
            allMutations.forEach((mutation) => {
                // Process removed tabs
                this.unregisterTabs(mutation.removedNodes);

                // Process added tabs
                mutation.addedNodes.forEach((node: HTMLElement) => this.registerTab(node));
            });

            // Adding / removing tabs might change the selected tab position. Ensure we refresh the indicator
            this.updateSelectionIndicator(this.selectedTab);
        });

        // Activate the mutation observer
        this.observer.observe(this.host, { subtree: true, childList: true });

        // Initially register all tabs that have been rendered already
        this.host.querySelectorAll("uic-tab").forEach((el: HTMLElement) => this.registerTab(el));
    }

    componentDidRender() {
        if (!this.manualSelectionIndicator) {
            //Needed for horizontal flyout menu (restoring history marker), doesn't change behavior for normal tab-list
            this.updateSelectionIndicator(this.selectedTab);
        }
    }

    disconnectedCallback() {
        if (this.observer) {
            this.observer.disconnect();
            this.observer = null;
        }
    }

    private registerTab(el: HTMLElement): void {
        const tab = el as HTMLUicTabElement;
        if (!tab || tab.nodeName !== "UIC-TAB") {
            return;
        }

        this.registeredTabs.push(tab);

        if (tab.value === this.value) {
            tab.selected = true;
        }
    }

    private unregisterTabs(nodes: NodeList): void {
        nodes.forEach((el: HTMLElement) => {
            const target = el as HTMLUicTabElement;
            const index = this.registeredTabs.indexOf(target);

            if (index > -1) {
                this.registeredTabs.splice(index, 1);
                if (this.selectedTab === target) {
                    this.setSelectedTab(undefined);
                }
            }
        });
    }

    private handleKeydown(event: KeyboardEvent) {
        if (this.isTop) {
            if (event.key === "Enter" || event.key === " ") {
                const tab = (event.target as HTMLElement).closest("uic-tab");

                if (tab) {
                    tab.selected = true;
                    event.preventDefault();
                }
                return;
            }

            const { code } = event;
            const step = stepsPerKey[this.vertical ? "vertical" : "horizontal"][code];

            if (step) {
                event.preventDefault();

                const registered = this.registeredTabs; // this list might be sorted improperly

                // get properly sorted list of tabs
                const candidates = this.host.querySelectorAll("uic-tab:not([disabled])");
                const numCandidates = candidates.length;

                // make sure focus is on one of the tabs
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                const focusedElement = document.activeElement as any;
                const focusedTabIndex = [].indexOf.call(candidates, focusedElement);

                if (focusedTabIndex > -1) {
                    // find next registered tab and focus
                    let nextIndex = focusedTabIndex;

                    do {
                        nextIndex = (focusedTabIndex + step + numCandidates) % numCandidates;

                        const candidate = candidates[nextIndex] as HTMLElement;

                        // eslint-disable-next-line @typescript-eslint/no-explicit-any
                        if (registered.includes(candidate as any)) {
                            candidate.focus();
                            break;
                        }
                    } while (nextIndex !== focusedTabIndex);
                }
            }
        }
    }

    private setSelectedTab(tab: HTMLUicTabElement) {
        const _tab = tab || undefined;

        // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
        if ((!_tab || !_tab.disabled) && _tab !== this.selectedTab) {
            if (this.selectedTab) {
                this.selectedTab.selected = false;
            }

            this.selectedTab = _tab;

            if (_tab) {
                _tab.selected = true;
                this.value = _tab.value;
            }

            if (!this.manualSelectionIndicator) {
                this.updateSelectionIndicator(this.selectedTab);
            }
        }
    }

    /**
     * Handles request of a subordinated uic-tab to become selected one.
     *
     * @param {PointerEvent} event event targeting tab to be selected
     */
    @Listen("uicTabSelect")
    onTabSelectionRequest(event: CustomEvent<void>): void {
        if (this.isTop) {
            const target = event.target as HTMLUicTabElement;

            if (this.registeredTabs.find((tab) => tab === target)) {
                this.setSelectedTab(target);
            }
        }
    }

    /**
     * If necessary, the selection indicator can be changed from the parent
     * component, for example when resetting the indicator to the parent-tab
     * that contains the last opened child-tab
     *
     * @param tab tab that the indicator moves to. if null, the indicator disappears.
     */
    @Method()
    async updateSelectionIndicator(tab: HTMLUicTabElement) {
        // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
        if (this.isTop && this.selectionIndicator && (!tab || !tab.disabled)) {
            const { style } = this.selectionIndicator;

            if (!tab) {
                style[this.vertical ? "height" : "width"] = "0";
                style.transform = `translateX(0px)`;
            } else {
                const tabBounds = tab.getBoundingClientRect();
                const listBounds = this.host.getBoundingClientRect();

                if (this.vertical) {
                    const height = tabBounds.height - 1;
                    const offset = tabBounds.top - listBounds.top;

                    style.height = `${height}px`;
                    style.transform = `translateY(${offset}px)`;
                } else {
                    const width = tabBounds.width;
                    const offset = tabBounds.left - listBounds.left;

                    style.width = `${width}px`;
                    style.transform = `translateX(${offset}px)`;
                }
            }
        }
    }

    /**
     * Delivers currently selected tab of current list.
     */
    @Method()
    async selected(): Promise<HTMLUicTabElement> {
        return this.selectedTab;
    }

    /**
     * Delivers currently selected tab of current list.
     */
    @Method()
    async unselectAll() {
        this.setSelectedTab(undefined);
    }

    render() {
        return (
            <Host
                role='tablist'
                class={{
                    "top-most": this.isTop,
                    nested: !this.isTop
                }}
            >
                <slot />
                <div class='uic-tab-list__indicator' ref={(el) => (this.selectionIndicator = el)} />
            </Host>
        );
    }
}
